import type { Ssm5RecentConnection } from '@packages/domain/ssm5';
import type { EnrichedVehicleDetails } from '@web/vin-sessions/types';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { Ssm5LiveConnection } from '../context/ssm5-connection.context';

import { toConnectionOffer } from '../helpers/connection-offer';
import { useConnectionStatus } from '../hooks/use-connection-status';
import { useSsm5Connection } from '../hooks/use-ssm5-connection';
import { Ssm5RecentConnectionCard } from './ssm5-recent-connection-card';
import { Ssm5SectionTitle } from './ssm5-section-title';

const useStyles = createUseStyles({
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },
});

/** Props for {@link Ssm5ConnectionOffer}. */
export type Ssm5ConnectionOfferProps = {
    /** The live session to offer back, or undefined to offer the recent connection instead. */
    live: Ssm5LiveConnection | undefined;
    /** The recent connection to offer when no session is live. */
    recentConnection: Ssm5RecentConnection | undefined;
    /** The vehicle of the recent connection, or undefined while it is unknown. */
    vehicle: EnrichedVehicleDetails | undefined;
};

/**
 * The Recent connections section of the SSM5 page: its title and the card of the connection offered
 * back. A connection still open on its Cloud Connector, live in this tab or reported open by the
 * backend, shows again without connecting; one that is gone reopens through the connect flow. Nothing
 * shows until the backend has answered.
 *
 * @param props - {@link Ssm5ConnectionOfferProps}
 * @returns the section, or null while there is nothing to offer
 */
export function Ssm5ConnectionOffer({ live, recentConnection, vehicle }: Ssm5ConnectionOfferProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const { connect, resume } = useSsm5Connection();
    const recentStatus = useConnectionStatus(live ? undefined : recentConnection?.cloudConnectorId);
    const offer = useMemo(
        () => toConnectionOffer(live, recentConnection, vehicle, recentStatus),
        [live, recentConnection, recentStatus, vehicle]
    );
    const connection = offer?.connection;

    const open = useCallback(() => {
        if (connection) {
            resume(connection);
        }
    }, [connection, resume]);

    if (!offer) {
        return null;
    }

    return (
        <section className={classes.section}>
            <Ssm5SectionTitle>{t('ssm5:recent.title')}</Ssm5SectionTitle>
            <Ssm5RecentConnectionCard
                isConnected={offer.isConnected}
                onOpen={offer.isConnected ? open : connect}
                recentConnection={offer.connection}
                vehicle={offer.connection.vehicle}
            />
        </section>
    );
}
